import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPlanning, updatePlanning, getProjet, resyncPlanning } from '../services/api';
import { Calendar, Download, Save, Plus, Trash2, ArrowLeft, Wrench, Truck, HardHat, Clock, MapPin, CalendarDays, Users, RefreshCw, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL;

// Icônes par type d'intervenant
const TYPE_ICONS = {
  artisan: { icon: Wrench, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Artisan' },
  fournisseur: { icon: Truck, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Fournisseur' },
  maag: { icon: HardHat, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'MAAG' }
};

// Sortable task row for planning
const SortableTaskRow = ({ id, children, className, style: extraStyle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : 'auto',
    ...extraStyle,
  };
  return (
    <tr ref={setNodeRef} style={style} className={className}>
      <td className="px-1 py-2 w-8">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500" data-testid="planning-drag-handle">
          <GripVertical className="w-4 h-4" />
        </button>
      </td>
      {children}
    </tr>
  );
};

const AUTOSAVE_MS = 3 * 60 * 1000;

const PlanningPage = () => {
  const { planningId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [planning, setPlanning] = useState(null);
  const [projet, setProjet] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [autosaveIn, setAutosaveIn] = useState(AUTOSAVE_MS / 1000);
  const savedSnapshotRef = useRef(null);
  const planningRef = useRef(null);
  planningRef.current = planning;
  const draftKey = `planning_draft_${planningId}`;

  const toPayload = (p) => p ? ({
    date_demarrage: p.date_demarrage,
    date_fin_travaux: p.date_fin_travaux,
    date_visite_bureau_controle: p.date_visite_bureau_controle,
    date_ouverture: p.date_ouverture,
    jour_reunion: p.jour_reunion,
    heure_reunion: p.heure_reunion,
    taches: (p.taches || []).map(({ _dndId, ...rest }) => rest),
    fournisseurs: p.fournisseurs
  }) : null;
  const isDirty = !!planning && !!savedSnapshotRef.current && JSON.stringify(toPayload(planning)) !== savedSnapshotRef.current;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Generate stable IDs for taches (used by dnd-kit)
  const tacheIds = useMemo(() => {
    if (!planning?.taches) return [];
    return planning.taches.map((t, i) => t._dndId || `tache-${i}`);
  }, [planning?.taches]);

  const handleTaskDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !planning) return;
    const oldIndex = tacheIds.indexOf(active.id);
    const newIndex = tacheIds.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newTaches = arrayMove([...planning.taches], oldIndex, newIndex);
    setPlanning({ ...planning, taches: newTaches });
  }, [planning, tacheIds]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPlanning(planningId);
      // Assign stable dnd IDs to taches
      const data = res.data;
      if (data.taches) {
        data.taches = data.taches.map((t, i) => ({ ...t, _dndId: t._dndId || `tache-${Date.now()}-${i}` }));
      }
      savedSnapshotRef.current = JSON.stringify(toPayload(data));
      // Brouillon local non sauvegardé (bug / session perdue) → proposer la restauration
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const draft = JSON.parse(raw);
          if (draft?.payload && JSON.stringify(draft.payload) !== savedSnapshotRef.current) {
            const when = new Date(draft.ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
            toast(`Brouillon non sauvegardé retrouvé (${when})`, {
              description: 'Des modifications du planning n\'avaient pas été enregistrées.',
              duration: 15000,
              action: {
                label: 'Restaurer',
                onClick: () => {
                  const taches = (draft.payload.taches || []).map((t, i) => ({ ...t, _dndId: `tache-${Date.now()}-${i}` }));
                  setPlanning((prev) => ({ ...prev, ...draft.payload, taches }));
                  toast.success('Brouillon restauré — pensez à sauvegarder');
                },
              },
              cancel: { label: 'Ignorer', onClick: () => localStorage.removeItem(draftKey) },
            });
          } else {
            localStorage.removeItem(draftKey);
          }
        }
      } catch (_) {}
      setPlanning(data);
      
      // Load projet for navigation
      const projetRes = await getProjet(res.data.projet_id);
      setProjet(projetRes.data);
    } catch (err) {
      toast.error('Erreur de chargement');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [planningId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async (opts = {}) => {
    const { silent = false } = opts;
    const current = planningRef.current;
    if (!current) return;
    try {
      setSaving(true);
      const payload = toPayload(current);
      const res = await updatePlanning(planningId, payload);
      localStorage.removeItem(draftKey);
      setLastSavedAt(new Date());
      setAutosaveIn(AUTOSAVE_MS / 1000);
      // Mise à jour en place (semaines recalculées) sans recharger la page → pas de perte de focus
      const data = res.data;
      const dndIds = (current.taches || []).map((t) => t._dndId);
      if (data?.taches) data.taches = data.taches.map((t, i) => ({ ...t, _dndId: dndIds[i] || `tache-${Date.now()}-${i}` }));
      const merged = { ...current, ...data };
      savedSnapshotRef.current = JSON.stringify(toPayload(merged));
      setPlanning(merged);
      if (silent) toast.success('Sauvegarde automatique effectuée', { duration: 2000 });
      else toast.success('Planning sauvegardé');
    } catch (err) {
      toast.error(silent ? 'Sauvegarde automatique impossible — vos modifications restent en brouillon local' : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Brouillon local à chaque modification (protection contre bug / session perdue)
  useEffect(() => {
    if (!planning || !savedSnapshotRef.current) return;
    const payload = toPayload(planning);
    if (JSON.stringify(payload) === savedSnapshotRef.current) { localStorage.removeItem(draftKey); return; }
    try { localStorage.setItem(draftKey, JSON.stringify({ ts: Date.now(), payload })); } catch (_) {}
  }, [planning]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sauvegarde automatique toutes les 3 min si modifications non enregistrées + compte à rebours
  useEffect(() => {
    const tick = setInterval(() => {
      setAutosaveIn((v) => {
        if (v > 1) return v - 1;
        const cur = planningRef.current;
        if (cur && savedSnapshotRef.current && JSON.stringify(toPayload(cur)) !== savedSnapshotRef.current) handleSave({ silent: true });
        return AUTOSAVE_MS / 1000;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Avertissement avant de quitter avec des modifications non enregistrées
  useEffect(() => {
    const onBeforeUnload = (e) => {
      const cur = planningRef.current;
      if (cur && savedSnapshotRef.current && JSON.stringify(toPayload(cur)) !== savedSnapshotRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [pdfLoading, setPdfLoading] = useState(false);
  const handleDownloadPDF = async () => {
    const token = localStorage.getItem('token');
    const raw = `Planning_${(planning?.projet_nom || planning?.enseigne_nom || 'MAAG').replace(/\s*-\s*/g, ' ').trim()}_V${planning?.version || '1'}.pdf`;
    const fname = raw.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9._-]+/g, '_').replace(/_+/g, '_');
    const url = `${API}/api/plannings/${planningId}/pdf/${encodeURIComponent(fname)}?token=${token}&t=${Date.now()}`;
    setPdfLoading(true);
    try {
      // Téléchargement en blob : le nom est imposé côté client (certains navigateurs/PWA ignorent Content-Disposition)
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], fname, { type: 'application/pdf' });
      const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent);
      if (isIOS && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: fname });
        return;
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
      toast.success(`PDF téléchargé : ${fname}`);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      window.open(url, '_blank');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleResync = async () => {
    if (!window.confirm('Resynchroniser depuis Marge/Devis ?\n\nLes entreprises seront mises à jour depuis l\'onglet Marge et les fournisseurs depuis les Fournisseurs Directs.\n\nVos planifications (semaines) seront conservées.')) {
      return;
    }
    try {
      setResyncing(true);
      const res = await resyncPlanning(planningId);
      setPlanning(res.data);
      toast.success('Planning resynchronisé avec les données Marge/Devis');
    } catch (err) {
      toast.error('Erreur lors de la resynchronisation');
    } finally {
      setResyncing(false);
    }
  };

  const updateTache = (index, field, value) => {
    const newTaches = [...planning.taches];
    newTaches[index] = { ...newTaches[index], [field]: value };
    setPlanning({ ...planning, taches: newTaches });
  };

  const toggleDay = (tacheIndex, semKey, day) => {
    const newTaches = [...planning.taches];
    const tache = newTaches[tacheIndex];
    if (!tache.semaines) tache.semaines = {};
    if (!tache.semaines[semKey]) tache.semaines[semKey] = {};
    tache.semaines[semKey][day] = !tache.semaines[semKey][day];
    setPlanning({ ...planning, taches: newTaches });
  };

  const addTache = () => {
    setPlanning({
      ...planning,
      taches: [...planning.taches, { lot: '', tache: '', entreprise: '', type_intervenant: 'artisan', semaines: {}, _dndId: `tache-${Date.now()}` }]
    });
  };

  const removeTache = (index) => {
    const newTaches = planning.taches.filter((_, i) => i !== index);
    setPlanning({ ...planning, taches: newTaches });
  };

  const updateFournisseur = (index, field, value) => {
    const newFourn = [...planning.fournisseurs];
    newFourn[index] = { ...newFourn[index], [field]: value };
    setPlanning({ ...planning, fournisseurs: newFourn });
  };

  const addFournisseur = () => {
    setPlanning({
      ...planning,
      fournisseurs: [...planning.fournisseurs, { fournisseur: '', produit_lot: '', interlocuteur: '', telephone: '', email: '', date_livraison: '', observation: '' }]
    });
  };

  const removeFournisseur = (index) => {
    const newFourn = planning.fournisseurs.filter((_, i) => i !== index);
    setPlanning({ ...planning, fournisseurs: newFourn });
  };

  // Calculer le tableau lecture rapide à partir des tâches
  const quickReadData = useMemo(() => {
    if (!planning?.taches || !planning?.semaines) return [];
    
    const jours = ['L', 'M', 'M2', 'J', 'V', 'S', 'D'];
    
    return planning.taches.map(tache => {
      let startDate = null;
      let endDate = null;
      let totalDays = 0;
      
      // Parcourir les semaines pour trouver les dates
      for (const sem of planning.semaines) {
        const semKey = `S${sem.numero}`;
        const semData = tache.semaines?.[semKey] || {};
        
        for (let dayIdx = 0; dayIdx < jours.length; dayIdx++) {
          const day = jours[dayIdx];
          if (semData[day]) {
            totalDays++;
            const dateStr = sem.dates?.[dayIdx];
            if (dateStr) {
              if (!startDate) startDate = dateStr;
              endDate = dateStr;
            }
          }
        }
      }
      
      return {
        lot: tache.lot,
        entreprise: tache.entreprise || '-',
        intervention: startDate && endDate ? `${startDate} → ${endDate}` : '-',
        duree: totalDays > 0 ? `${totalDays} jour${totalDays > 1 ? 's' : ''}` : '-',
        type: tache.type_intervenant || 'artisan'
      };
    }).filter(row => row.intervention !== '-'); // Ne montrer que les lignes avec des dates
  }, [planning]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!planning) return null;

  const jours = ['L', 'M', 'M2', 'J', 'V', 'S', 'D'];
  const joursLabels = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

  // Fonction pour formater une date pour l'input
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    // Si déjà au format YYYY-MM-DD, retourner tel quel
    if (dateStr.includes('-') && dateStr.length === 10) return dateStr;
    // Sinon convertir de DD/MM/YYYY vers YYYY-MM-DD
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return '';
  };

  // Fonction pour formater une date pour l'affichage
  const formatDateFromInput = (val) => {
    if (!val) return '';
    return val.split('-').reverse().join('/');
  };

  // Changement de la date de démarrage : les dates suivantes vides (ou antérieures) sont pré-remplies
  // → le calendrier natif s'ouvre directement sur le bon mois (iOS/Chrome ouvrent sur la valeur, sinon sur aujourd'hui)
  const withDatesSuivantes = (p, isoDemarrage) => {
    const next = { ...p, date_demarrage: formatDateFromInput(isoDemarrage) };
    if (!isoDemarrage) return next;
    const plusJours = (n) => { const d = new Date(`${isoDemarrage}T12:00:00`); d.setDate(d.getDate() + n); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`; };
    const avant = (fr) => !fr || formatDateForInput(fr) < isoDemarrage;
    if (avant(p.date_fin_travaux)) next.date_fin_travaux = plusJours(42);
    if (avant(p.date_visite_bureau_controle)) next.date_visite_bureau_controle = next.date_fin_travaux;
    if (avant(p.date_ouverture)) next.date_ouverture = plusJours(56);
    return next;
  };

  // Normalise "DD/MM/YYYY" ou "YYYY-MM-DD" → "YYYY-MM-DD" (clé de comparaison Gantt / livraisons)
  const toIsoDate = (s) => {
    if (!s) return '';
    if (s.includes('-') && s.length >= 10) return s.slice(0, 10);
    const p = s.split('/');
    return p.length === 3 ? `${p[2]}-${p[1].padStart(2, '0')}-${p[0].padStart(2, '0')}` : '';
  };
  // Date ISO du jour dIdx (0 = lundi) d'une semaine du planning (sem.start_date = lundi "YYYY-MM-DD")
  const isoFromWeek = (sem, dIdx) => {
    if (!sem?.start_date) return '';
    const d = new Date(`${sem.start_date}T00:00:00`);
    d.setDate(d.getDate() + dIdx);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const livraisonsParJour = new Map();
  (planning?.fournisseurs || []).forEach((f) => {
    const iso = toIsoDate(f.date_livraison);
    if (!iso) return;
    const label = (f.produit_lot || f.fournisseur || 'fournisseur').trim();
    livraisonsParJour.set(iso, [...(livraisonsParJour.get(iso) || []), label]);
  });
  const livraisonDates = [...livraisonsParJour.keys()].sort();
  // Voies sans chevauchement (web) : jour = 14 px (w-3 + gap), libellé ≈ 5 px / caractère
  const livraisonLane = new Map();
  {
    const dayPx = 14;
    const dayIndex = new Map();
    (planning?.semaines || []).forEach((sem, sIdx) => { for (let d = 0; d < 7; d++) dayIndex.set(isoFromWeek(sem, d), sIdx * 7 + d); });
    const laneEnds = [];
    livraisonDates.forEach((iso) => {
      const idx = dayIndex.get(iso);
      if (idx === undefined) return;
      const x0 = idx * dayPx;
      const widest = Math.max(...livraisonsParJour.get(iso).map((l) => `Livraison ${l}`.length));
      const x1 = x0 + 18 + widest * 5;
      let lane = laneEnds.findIndex((end) => end <= x0);
      if (lane === -1) { laneEnds.push(x1); lane = laneEnds.length - 1; } else { laneEnds[lane] = x1; }
      livraisonLane.set(iso, lane);
    });
  }
  const nbLanes = Math.max(1, ...[...livraisonLane.values()].map((l) => l + 1));

  return (
    <div className="min-h-screen bg-slate-50 pb-10">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-[1800px] mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(`/projets/${planning.projet_id}`)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
              </Button>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" />
                <h1 className="text-lg font-bold text-slate-900">
                  Planning V{planning.version} - {planning.enseigne_nom}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleResync} 
                disabled={resyncing}
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                data-testid="resync-planning-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${resyncing ? 'animate-spin' : ''}`} /> 
                {resyncing ? 'Sync...' : 'Sync Marge'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={pdfLoading} data-testid="download-pdf-btn">
                <Download className={`w-4 h-4 mr-1 ${pdfLoading ? 'animate-bounce' : ''}`} /> {pdfLoading ? 'PDF…' : 'PDF'}
              </Button>
              <span className={`hidden md:inline-flex items-center text-xs px-2 py-1 rounded-md ${isDirty ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'text-slate-500'}`} data-testid="planning-save-status">
                {isDirty
                  ? `Non enregistré · auto dans ${Math.floor(autosaveIn / 60)}:${String(autosaveIn % 60).padStart(2, '0')}`
                  : lastSavedAt ? `Enregistré à ${lastSavedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : 'Tout est enregistré'}
              </span>
              <Button size="sm" onClick={() => handleSave()} disabled={saving} className={`${isDirty ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'}`} data-testid="save-planning-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1800px] mx-auto px-4 py-6 space-y-6">
        
        {/* ======================= SECTION 1: BANDEAU DATES CLÉS ======================= */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="key-dates-banner">
          <div className="bg-gradient-to-r from-indigo-900 to-indigo-700 px-4 py-2">
            <h2 className="text-white font-semibold text-sm flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              DATES CLÉS DU CHANTIER
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 divide-x divide-slate-200">
            {/* Démarrage Travaux */}
            <div className="p-4 bg-blue-50/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-xs font-bold text-blue-700 uppercase">Démarrage Travaux</span>
              </div>
              <Input
                type="date"
                value={formatDateForInput(planning.date_demarrage)}
                onChange={(e) => setPlanning(withDatesSuivantes(planning, e.target.value))}
                className="h-10 text-base font-semibold border-blue-200 focus:border-blue-400"
                data-testid="date-demarrage-input"
              />
              {planning.date_demarrage && (
                <p className="text-lg font-bold text-blue-900 mt-1">{planning.date_demarrage}</p>
              )}
            </div>
            
            {/* Fin Travaux */}
            <div className="p-4 bg-orange-50/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <Wrench className="w-4 h-4 text-orange-600" />
                </div>
                <span className="text-xs font-bold text-orange-700 uppercase">Fin Travaux</span>
              </div>
              <Input
                type="date"
                value={formatDateForInput(planning.date_fin_travaux)}
                min={formatDateForInput(planning.date_demarrage) || undefined}
                onChange={(e) => setPlanning({ ...planning, date_fin_travaux: formatDateFromInput(e.target.value) })}
                className="h-10 text-base font-semibold border-orange-200 focus:border-orange-400"
                data-testid="date-fin-travaux-input"
              />
              {planning.date_fin_travaux && (
                <p className="text-lg font-bold text-orange-900 mt-1">{planning.date_fin_travaux}</p>
              )}
            </div>
            
            {/* Visite Bureau de Contrôle */}
            <div className="p-4 bg-cyan-50/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                  <CalendarDays className="w-4 h-4 text-cyan-600" />
                </div>
                <span className="text-xs font-bold text-cyan-700 uppercase">Visite Bureau Ctrl</span>
              </div>
              <Input
                type="date"
                value={formatDateForInput(planning.date_visite_bureau_controle)}
                min={formatDateForInput(planning.date_demarrage) || undefined}
                onChange={(e) => setPlanning({ ...planning, date_visite_bureau_controle: formatDateFromInput(e.target.value) })}
                className="h-10 text-base font-semibold border-cyan-200 focus:border-cyan-400"
                data-testid="date-visite-bureau-controle-input"
              />
              {planning.date_visite_bureau_controle && (
                <p className="text-lg font-bold text-cyan-900 mt-1">{planning.date_visite_bureau_controle}</p>
              )}
            </div>
            
            {/* Ouverture Magasin */}
            <div className="p-4 bg-emerald-50/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="text-xs font-bold text-emerald-700 uppercase">Ouverture Magasin</span>
              </div>
              <Input
                type="date"
                value={formatDateForInput(planning.date_ouverture)}
                min={formatDateForInput(planning.date_demarrage) || undefined}
                onChange={(e) => setPlanning({ ...planning, date_ouverture: formatDateFromInput(e.target.value) })}
                className="h-10 text-base font-semibold border-emerald-200 focus:border-emerald-400"
                data-testid="date-ouverture-input"
              />
              {planning.date_ouverture && (
                <p className="text-lg font-bold text-emerald-900 mt-1">{planning.date_ouverture}</p>
              )}
            </div>
            
            {/* Réunion de Chantier */}
            <div className="p-4 bg-purple-50/50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-xs font-bold text-purple-700 uppercase">Réunion Chantier</span>
              </div>
              <div className="flex gap-2">
                <select
                  value={planning.jour_reunion}
                  onChange={(e) => setPlanning({ ...planning, jour_reunion: e.target.value })}
                  className="h-10 text-sm font-semibold border border-purple-200 rounded-md px-2 bg-white flex-1"
                  data-testid="jour-reunion-select"
                >
                  {['LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI'].map(j => (
                    <option key={j} value={j}>{j}</option>
                  ))}
                </select>
                <Input
                  value={planning.heure_reunion}
                  onChange={(e) => setPlanning({ ...planning, heure_reunion: e.target.value })}
                  className="h-10 text-sm font-semibold w-20 border-purple-200"
                  placeholder="10h"
                  data-testid="heure-reunion-input"
                />
              </div>
              <p className="text-base font-bold text-purple-900 mt-1">
                {planning.jour_reunion} à {planning.heure_reunion}
              </p>
            </div>
          </div>
          
          {/* Adresse du chantier */}
          <div className="px-4 py-2 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">{planning.adresse || 'Adresse non renseignée'}</span>
            </div>
          </div>
        </div>

        {/* ======================= SECTION 2: TABLEAU LECTURE RAPIDE ======================= */}
        {quickReadData.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="quick-read-table">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-2">
              <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                <Clock className="w-4 h-4" />
                LECTURE RAPIDE CHANTIER
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Lot</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Entreprise</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Intervention</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Durée</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {quickReadData.map((row, idx) => {
                    const typeInfo = TYPE_ICONS[row.type] || TYPE_ICONS.artisan;
                    const TypeIcon = typeInfo.icon;
                    return (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-4 py-3">
                          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${typeInfo.bg}`}>
                            <TypeIcon className={`w-3.5 h-3.5 ${typeInfo.color}`} />
                            <span className={`text-xs font-semibold ${typeInfo.color}`}>{typeInfo.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-slate-900">{row.lot}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{row.entreprise}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-indigo-700">{row.intervention}</td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-600">{row.duree}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ======================= SECTION 3: DIAGRAMME DE GANTT ======================= */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="gantt-chart">
          <div className="bg-gradient-to-r from-indigo-800 to-indigo-600 px-4 py-2">
            <h2 className="text-white font-semibold text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              PLANNING GANTT
            </h2>
          </div>
          <div className="overflow-x-auto">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTaskDragEnd}>
            <table className="w-full min-w-[1400px]">
              <thead>
                <tr className="bg-indigo-900 text-white">
                  <th className="px-1 py-3 w-8"></th>
                  <th className="px-2 py-3 text-left text-xs font-bold w-10">TYPE</th>
                  <th className="px-2 py-3 text-left text-xs font-bold w-28">LOT</th>
                  <th className="px-2 py-3 text-left text-xs font-bold w-64">TÂCHE</th>
                  <th className="px-2 py-3 text-left text-xs font-bold w-28">ENTREPRISE</th>
                  {planning.semaines.map(sem => (
                    <th key={sem.numero} className="px-1 py-3 text-center text-xs font-bold" style={{ minWidth: '90px' }}>
                      <div className="text-sm">S{sem.numero}</div>
                      <div className="text-[10px] font-normal opacity-80">{sem.mois.slice(0, 3)}</div>
                    </th>
                  ))}
                  <th className="w-10"></th>
                </tr>
                <tr className="bg-indigo-100 border-y border-indigo-300 sticky top-0 z-10">
                  <th></th>
                  <th colSpan="4"></th>
                  {planning.semaines.map(sem => (
                    <th key={`days-${sem.numero}`} className="px-0 py-1 text-center">
                      <div className="flex justify-center gap-0.5 text-[10px] text-indigo-900 font-bold leading-tight">
                        {joursLabels.map((j, i) => {
                          const rawDate = sem.dates?.[i];
                          let dayNum = '';
                          if (rawDate) {
                            // Support "YYYY-MM-DD" or "DD/MM/YYYY"
                            if (rawDate.includes('-') && rawDate.length >= 10) {
                              dayNum = parseInt(rawDate.slice(8, 10), 10);
                            } else if (rawDate.includes('/')) {
                              dayNum = parseInt(rawDate.split('/')[0], 10);
                            }
                          }
                          const isWeekend = i >= 5;
                          return (
                            <span
                              key={i}
                              className={`w-3 flex flex-col items-center ${isWeekend ? 'text-rose-600' : ''}`}
                              title={['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'][i]}
                            >
                              <span className="text-[8px] font-medium opacity-80">{dayNum || ''}</span>
                              <span className="text-[10px]">{j}</span>
                            </span>
                          );
                        })}
                      </div>
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <SortableContext items={tacheIds} strategy={verticalListSortingStrategy}>
              <tbody>
                {livraisonsParJour.size > 0 && (
                  <tr className="bg-orange-50 border-b border-orange-200" data-testid="gantt-livraisons-row" style={{ height: `${Math.max(44, nbLanes * 16 + 12)}px` }}>
                    <td></td>
                    <td colSpan="4" className="px-2 py-1">
                      <div className="flex items-center gap-2 text-xs font-bold text-orange-700 uppercase"><Truck className="w-4 h-4" /> Livraisons fournisseurs</div>
                      <div className="text-[10px] text-orange-600">{livraisonsParJour.size} date(s) · carré orange = jour de livraison</div>
                    </td>
                    {planning.semaines.map(sem => (
                      <td key={`livr-${sem.numero}`} className="px-0 py-1 relative align-top">
                        <div className="flex justify-center gap-0.5">
                          {jours.map((day, dIdx) => {
                            const iso = isoFromWeek(sem, dIdx);
                            const livs = iso ? livraisonsParJour.get(iso) : null;
                            const lane = livs ? (livraisonLane.get(iso) || 0) : 0;
                            return (
                              <div key={day} className="w-3 relative" style={{ height: `${nbLanes * 16}px` }}>
                                {livs && (
                                  <>
                                    <div className="w-3 h-3.5 rounded-sm bg-orange-500 shadow-md absolute left-0 z-30" style={{ top: `${lane * 16}px` }} data-testid={`gantt-livraison-${iso}`} title={livs.map(l => `Livraison ${l}`).join('\n')} />
                                    <div className="absolute left-4 z-20 whitespace-nowrap text-[9px] font-bold text-orange-800 bg-orange-50/95 px-1 rounded leading-[14px] pointer-events-none" style={{ top: `${lane * 16}px` }}>
                                      {livs.map((l, k) => <span key={k}>{k > 0 ? ' · ' : ''}Livraison {l}</span>)}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    ))}
                    <td></td>
                  </tr>
                )}
                {planning.taches.map((tache, tIdx) => {
                  const typeInfo = TYPE_ICONS[tache.type_intervenant] || TYPE_ICONS.artisan;
                  const TypeIcon = typeInfo.icon;
                  return (
                    <SortableTaskRow key={tacheIds[tIdx]} id={tacheIds[tIdx]} className={`${tIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-indigo-50/30 transition-colors`} style={{ height: '48px' }}>
                      {/* Type Intervenant */}
                      <td className="px-2 py-2 relative">
                        <div 
                          className={`w-9 h-9 rounded-full ${typeInfo.bg} flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-${typeInfo.color.replace('text-', '')}`}
                          onClick={() => {
                            const types = ['artisan', 'fournisseur', 'maag'];
                            const currentIdx = types.indexOf(tache.type_intervenant || 'artisan');
                            const nextIdx = (currentIdx + 1) % types.length;
                            updateTache(tIdx, 'type_intervenant', types[nextIdx]);
                          }}
                          title={`${typeInfo.label} - Cliquer pour changer`}
                        >
                          <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                        </div>
                      </td>
                      {/* Lot */}
                      <td className="px-2 py-2">
                        <Input
                          value={tache.lot}
                          onChange={(e) => updateTache(tIdx, 'lot', e.target.value)}
                          className="h-9 text-sm font-bold border-slate-200"
                          placeholder="LOT"
                        />
                      </td>
                      {/* Tâche */}
                      <td className="px-2 py-2">
                        <Input
                          value={tache.tache}
                          onChange={(e) => updateTache(tIdx, 'tache', e.target.value)}
                          className="h-9 text-sm border-slate-200"
                          placeholder="Description de la tâche"
                        />
                      </td>
                      {/* Entreprise */}
                      <td className="px-2 py-2">
                        <Input
                          value={tache.entreprise}
                          onChange={(e) => updateTache(tIdx, 'entreprise', e.target.value)}
                          className="h-9 text-sm border-slate-200"
                          placeholder="Entreprise"
                        />
                      </td>
                      {/* Semaines Gantt */}
                      {planning.semaines.map(sem => {
                        const semKey = `S${sem.numero}`;
                        const semData = tache.semaines?.[semKey] || {};
                        return (
                          <td key={semKey} className="px-0 py-2">
                            <div className="flex justify-center gap-0.5">
                              {jours.map((day, dIdx) => (
                                <button
                                  key={day}
                                  onClick={() => toggleDay(tIdx, semKey, day)}
                                  className={`w-3 h-7 text-[8px] font-bold rounded transition-all ${
                                    semData[day] 
                                      ? 'bg-indigo-600 text-white shadow-md scale-105' 
                                      : 'bg-slate-200 text-transparent hover:bg-slate-300'
                                  }`}
                                  title={`${joursLabels[dIdx]} S${sem.numero}`}
                                >
                                  ●
                                </button>
                              ))}
                            </div>
                          </td>
                        );
                      })}
                      {/* Delete */}
                      <td className="px-1">
                        <button
                          onClick={() => removeTache(tIdx)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </SortableTaskRow>
                  );
                })}
              </tbody>
              </SortableContext>
            </table>
            </DndContext>
          </div>
          <div className="p-3 border-t border-slate-100 bg-slate-50">
            <Button variant="outline" size="sm" onClick={addTache} className="gap-1" data-testid="add-task-btn">
              <Plus className="w-4 h-4" /> Ajouter une tâche
            </Button>
          </div>
          
          {/* Légende */}
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center gap-6">
            <span className="text-xs font-bold text-slate-500 uppercase">Légende :</span>
            <div className="flex items-center gap-2">
              <div className="w-6 h-4 rounded bg-blue-100 flex items-center justify-center">
                <Wrench className="w-3 h-3 text-blue-600" />
              </div>
              <span className="text-xs text-slate-600">Artisan</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-4 rounded bg-amber-100 flex items-center justify-center">
                <Truck className="w-3 h-3 text-amber-600" />
              </div>
              <span className="text-xs text-slate-600">Fournisseur</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-4 rounded bg-emerald-100 flex items-center justify-center">
                <HardHat className="w-3 h-3 text-emerald-600" />
              </div>
              <span className="text-xs text-slate-600">MAAG</span>
            </div>
            <div className="flex items-center gap-2 ml-6">
              <div className="w-4 h-4 rounded bg-indigo-600"></div>
              <span className="text-xs text-slate-600">Jour planifié</span>
            </div>
          </div>
        </div>

        {/* ======================= SECTION 4: TABLEAU FOURNISSEURS ======================= */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="suppliers-table">
          <div className="bg-gradient-to-r from-amber-700 to-amber-600 px-4 py-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-white font-semibold text-sm flex items-center gap-2">
              <Truck className="w-4 h-4" />
              LIVRAISONS FOURNISSEURS
              <span className="text-xs font-normal text-amber-100" data-testid="suppliers-count">({planning.fournisseurs.length})</span>
            </h2>
            <span className={`text-xs px-2 py-1 rounded-md ${planning.fournisseurs.length > 12 ? 'bg-white text-amber-800 font-semibold' : 'text-amber-100'}`} data-testid="suppliers-pdf-info">
              {planning.fournisseurs.length > 12
                ? `PDF : 12 livraisons en page 1 + liste complète des ${planning.fournisseurs.length} en page 3`
                : `PDF : les ${planning.fournisseurs.length} livraisons tiennent en page 1 (12 max)`}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-amber-50">
                  <th className="px-3 py-3 text-left text-xs font-bold text-amber-800 uppercase tracking-wider">Fournisseur</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-amber-800 uppercase tracking-wider">Produit / Lot</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-amber-800 uppercase tracking-wider w-32">Date Livraison</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-amber-800 uppercase tracking-wider">Interlocuteur</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-amber-800 uppercase tracking-wider">Téléphone</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-amber-800 uppercase tracking-wider">Email</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-amber-800 uppercase tracking-wider">Observation</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-amber-100">
                {planning.fournisseurs.map((f, fIdx) => (
                  <tr key={fIdx} className={`${fIdx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'} hover:bg-amber-50/50 transition-colors`}>
                    <td className="px-2 py-2">
                      <Input 
                        value={f.fournisseur} 
                        onChange={(e) => updateFournisseur(fIdx, 'fournisseur', e.target.value)} 
                        className="h-9 text-sm font-semibold border-amber-200" 
                        placeholder="Nom fournisseur"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input 
                        value={f.produit_lot || ''} 
                        onChange={(e) => updateFournisseur(fIdx, 'produit_lot', e.target.value)} 
                        className="h-9 text-sm border-amber-200" 
                        placeholder="Produit / Lot"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input 
                        type="date"
                        value={formatDateForInput(f.date_livraison)} 
                        onChange={(e) => updateFournisseur(fIdx, 'date_livraison', formatDateFromInput(e.target.value))} 
                        className="h-9 text-sm font-bold text-red-700 border-amber-200" 
                        data-testid={`fournisseur-date-livraison-${fIdx}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input 
                        value={f.interlocuteur} 
                        onChange={(e) => updateFournisseur(fIdx, 'interlocuteur', e.target.value)} 
                        className="h-9 text-sm border-amber-200" 
                        placeholder="Contact"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input 
                        value={f.telephone} 
                        onChange={(e) => updateFournisseur(fIdx, 'telephone', e.target.value)} 
                        className="h-9 text-sm border-amber-200" 
                        placeholder="Téléphone"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input 
                        value={f.email} 
                        onChange={(e) => updateFournisseur(fIdx, 'email', e.target.value)} 
                        className="h-9 text-sm border-amber-200" 
                        placeholder="email@exemple.com"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input 
                        value={f.observation || ''} 
                        onChange={(e) => updateFournisseur(fIdx, 'observation', e.target.value)} 
                        className="h-9 text-sm border-amber-200" 
                        placeholder="Notes"
                      />
                    </td>
                    <td className="px-1">
                      <button 
                        onClick={() => removeFournisseur(fIdx)} 
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-amber-100 bg-amber-50/50">
            <Button variant="outline" size="sm" onClick={addFournisseur} className="gap-1" data-testid="add-supplier-btn">
              <Plus className="w-4 h-4" /> Ajouter un fournisseur
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanningPage;
